/**
 * EmailTestSendPage — /admin/email-test-send.
 *
 * **No longer previews.** ADR-0051 moved email bodies into Mailchimp,
 * which previews them properly — in its own builder, with the real
 * wrapper, against real inbox rendering. The two panes this page used to
 * show rendered Python copy modules that no longer exist.
 *
 * What is left is the half Mailchimp cannot do: send a real email through
 * *our* payload. A Mailchimp test send proves the template renders. This
 * proves registry resolution, merge-var validation, the subaccount, the
 * tag set and the draft/published selector — the parts that break on a
 * deploy and are invisible from the vendor UI.
 *
 * Two things kept from the original, both still right:
 *
 * 1. **The allowlist is displayed, not implied.** The operator should
 *    never have to guess which addresses a test send may target.
 * 2. **Merge-var fields are prefilled from the declared examples**, so
 *    the common case is pick a kind, type your address, send.
 *
 * To see what an email looks like, run
 * `apps/api-py/scripts/render_email_template.py`.
 */
import { useRefreshBlocker } from '@speaking-coach/ui'
import { SectionHeader } from '../common/section-header'
import { FieldError, Icon, Input, InputGroup, InputGroupButton, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@speaking-coach/ui'
import { PendingSpinner } from '../common/save-button'
import { PageHeader } from '../components/page-header'
import { OPERATOR_PAGE_MAIN } from '../common/page-layout'
import { useCallback, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { queryKeys } from '../api/query-keys'

import { AdminApiError } from '../api/client.js'
import { FormError, formMessage, messageFor, resolveFailure } from '../common/field-errors'
import { adminEmailTestSendClient } from './api.js'
import { initialSampleVars, type SendableEmail } from './types.js'

type EmailTestSendField = 'kind' | 'recipient'

const EMAIL_TEST_SEND_ERROR_FIELD: Readonly<
  Record<string, EmailTestSendField>
> = {
  unknown_kind: 'kind',
  recipient_not_allowlisted: 'recipient',
}

/**
 * MIRRORS `email_test_send/service.is_recipient_allowlisted` — exactly one
 * `@`, non-empty local part, exact (case-insensitive) domain match; a
 * suffix match would accept `attacker@cooori.com.evil.test`. Change the two
 * together. The server stays the authority (its allowlist can change
 * between page load and send), so its 422 still renders at the field.
 */
function isRecipientAllowlisted(
  recipient: string,
  domains: readonly string[],
): boolean {
  const candidate = recipient.trim()
  const parts = candidate.split('@')
  if (parts.length !== 2) return false
  const [local, domain] = parts
  if (!local || !domain) return false
  return domains.includes(domain.toLowerCase())
}

function errorText(err: unknown, fallback: string): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`
  if (err instanceof Error) return err.message
  return fallback
}

export function EmailTestSendPage(): JSX.Element {
  const [selectedKind, setSelectedKind] = useState<string | null>(null)
  const [sampleVars, setSampleVars] = useState<Record<string, string>>({})
  const [recipient, setRecipient] = useState<string>('')
  useRefreshBlocker(selectedKind !== null || recipient !== '' || Object.keys(sampleVars).length > 0)
  const [recipientError, setRecipientError] = useState<string | null>(null)

  const templatesQuery = useQuery({
    queryKey: queryKeys.emailTemplates,
    queryFn: () => adminEmailTestSendClient.templates(),
  })

  // `null` while loading, `[]` on failure — the page shows the error and an
  // empty catalog rather than a spinner that never resolves.
  const templates: SendableEmail[] | null = templatesQuery.isPending
    ? null
    : (templatesQuery.data?.templates ?? [])
  const allowedDomains = templatesQuery.data?.allowedRecipientDomains ?? []
  const loadError =
    templatesQuery.error !== null
      ? errorText(templatesQuery.error, 'failed to load templates')
      : null

  const send = useMutation({
    mutationFn: (kind: string) =>
      adminEmailTestSendClient.sendTest({ kind, sampleVars, recipient }),
  })

  const sending = send.isPending
  const sendResult = send.data?.messageId ?? null
  const sendFailure =
    send.error !== null
      ? resolveFailure(
          send.error,
          EMAIL_TEST_SEND_ERROR_FIELD,
          (err) => errorText(err, 'failed to send test email'),
        )
      : null

  /** Pick a kind and reset its form to the declared examples. */
  const selectKind = useCallback(
    (kind: string, catalog: SendableEmail[]): void => {
      const template = catalog.find((t) => t.key === kind)
      if (!template) return
      setSelectedKind(kind)
      setSampleVars(initialSampleVars(template))
      // A previous send's result belongs to the previous template.
      send.reset()
    },
    [send],
  )

  // Deliberately NO auto-selected template: which email gets sent is the
  // one decision this page exists for, and the first catalog entry
  // arriving silently pre-answered read as arbitrary — the same rule as
  // the client picker's missing home-client fallback. The send step
  // renders once an email is chosen.

  function sendTest(): void {
    if (selectedKind === null) return
    if (!/^\S+@\S+\.\S+$/.test(recipient.trim())) {
      setRecipientError('Enter a valid email address.')
      return
    }
    // Pre-check the allowlist the page is already displaying, so a wrong
    // domain is refused where the operator is looking instead of costing a
    // round trip. Skipped when the catalog failed to load (`domains`
    // unknown) — then the server answers.
    if (
      allowedDomains.length > 0 &&
      !isRecipientAllowlisted(recipient, allowedDomains)
    ) {
      setRecipientError(
        `Not an allowed domain. Test sends can go to: ${allowedDomains.join(', ')}.`,
      )
      return
    }
    setRecipientError(null)
    send.mutate(selectedKind)
  }

  const selectedTemplate = templates?.find((t) => t.key === selectedKind) ?? null

  return (
    <main className={`${OPERATOR_PAGE_MAIN} text-text-primary`}>
      <div className="max-w-3xl mx-auto space-y-6">
        <PageHeader title="Email test send" />
        <div className="space-y-1 text-text-secondary">
          <p className="text-body">
            Send one real copy of a transactional email to your own inbox, through the
            same code path a student&rsquo;s email takes.
          </p>
          <p className="text-caption">
            Email content is authored and previewed in Mailchimp. This page checks the
            wiring — template resolution, merge variables, tags — not the design.
          </p>
        </div>

        {loadError !== null && (
          <p
            role="alert"
            data-testid="email-test-send-load-error"
            className="text-body text-danger-text"
          >
            {loadError}
          </p>
        )}

        {templates === null ? (
          <p data-testid="email-test-send-loading" className="text-body text-text-secondary">
            Loading templates…
          </p>
        ) : (
          <form
            className="space-y-6"
            aria-label="Email test send"
            noValidate
            onSubmit={(e) => {
              e.preventDefault()
              sendTest()
            }}
          >
            <FormError message={formMessage(sendFailure)} />
            <section className="space-y-3" data-testid="email-test-send-controls">
              <div className="text-body space-y-1">
                <label htmlFor="email-test-send-kind" className="text-text-secondary">
                  Email
                </label>
                {/* `required`: '' stays the primitive's no-value, so the
                    placeholder shows until the operator chooses. */}
                <Select
                  required
                  value={selectedKind ?? ''}
                  onValueChange={(kind) => selectKind(kind, templates)}
                >
                  <SelectTrigger
                    id="email-test-send-kind"
                    surface="raised"
                    data-testid="email-test-send-kind"
                  >
                    <SelectValue
                      placeholder={
                        templates.length === 0
                          ? 'No templates'
                          : 'Choose an email'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.key} value={t.key}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError message={messageFor(sendFailure, 'kind')} />
                {selectedKind === null && (
                  <p className="text-caption text-text-secondary">
                    Pick an email first — its variables and the send step
                    appear here.
                  </p>
                )}
              </div>

              {/* The variables belong to the CHOSEN template, not to the
                  page — their own titled block says so, where a flat run of
                  fields read as siblings of the Email picker. */}
              {selectedTemplate !== null &&
                selectedTemplate.sampleVars.length > 0 && (
                  <section
                    aria-label="Template variables"
                    className="space-y-3 rounded-token-card bg-surface-card p-6"
                  >
                    <SectionHeader title="Template variables" meta={<Icon name="templateVariables" />} />
                    <p className="text-caption text-text-secondary">
                      Prefilled with sample values. All are required — a blank
                      one arrives as an empty string.
                    </p>
                    {selectedTemplate.sampleVars.map((v) => {
                      const inputId = `email-test-send-var-${v.name}`
                      return (
                        <div key={v.name} className="text-body space-y-1">
                          <div className="flex items-baseline justify-between gap-2">
                            {/* `font-medium` primary, like every other
                                card form's labels — the page's gray
                                labels belong to its bare-canvas fields. */}
                            <label htmlFor={inputId} className="font-medium">
                              {v.name}
                            </label>
                            {/* The SAME variable as the Mailchimp editor
                                spells it — `reset_url` here is *|RESET_URL|*
                                there — so an operator can cross-reference
                                the template without knowing the mapping. */}
                            <span
                              className="font-mono text-caption text-text-faint"
                              title="How this variable appears in the Mailchimp template editor"
                            >
                              *|{v.name.toUpperCase()}|*
                            </span>
                          </div>
                          {/* Default (inset) surface: these sit on a card
                              now — `raised` is for fields on the gray app
                              canvas, like the Email picker above. */}
                          <Input
                            id={inputId}
                            data-testid={`email-test-send-var-${v.name}`}
                            value={sampleVars[v.name] ?? ''}
                            onChange={(e) =>
                              setSampleVars((prev) => ({
                                ...prev,
                                [v.name]: e.target.value,
                              }))
                            }
                            className="font-mono"
                          />
                        </div>
                      )
                    })}
                  </section>
                )}
            </section>

            {/* Progressive disclosure, like the batch form: the send step
                appears once there is something to send — no disabled button
                whose reason lives elsewhere. */}
            {selectedKind !== null && (
            <section className="space-y-2 border-t border-border-default pt-4">
              <SectionHeader title="Send a test" />
              <p
                data-testid="email-test-send-allowlist"
                className="text-caption text-text-secondary"
              >
                Allowed recipient domains: {allowedDomains.join(', ') || '(none configured)'}
              </p>
              <div className="space-y-1">
                <label htmlFor="email-test-send-recipient" className="text-body text-text-secondary">
                  Recipient
                </label>
                {/* One BLOCK, not a field beside a button: the input drops
                    its right radius, the action its left, and the pair share
                    a seam at the field's 40px height (Raphaël, 2026-08-07 —
                    the gapped version read as two unrelated controls). */}
                <InputGroup className="flex items-center">
                  <Input
                    id="email-test-send-recipient"
                    data-testid="email-test-send-recipient"
                    type="email"
                    placeholder={`you@${allowedDomains[0] ?? 'cooori.com'}`}
                    value={recipient}
                    onChange={(e) => {
                      setRecipient(e.target.value)
                      // The message answers the value it was ABOUT.
                      setRecipientError(null)
                    }}
                    aria-invalid={recipientError !== null || undefined}
                    surface="raised"
                    className="flex-1 rounded-r-none"
                  />
                  <InputGroupButton
                    type="submit"
                    data-testid="email-test-send-send"
                    disabled={sending || recipient.trim() === ''}
                    pending={sending}
                    variant="primary"
                  >
                    {sending ? (
                      <>
                        <PendingSpinner surface="accent" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <Icon name="send" />
                        Send test
                      </>
                    )}
                  </InputGroupButton>
                </InputGroup>
                <FieldError
                  message={recipientError ?? messageFor(sendFailure, 'recipient')}
                />
              </div>
              {sendResult !== null && (
                <p
                  role="status"
                  data-testid="email-test-send-send-result"
                  className="text-body"
                >
                  Sent. Message id: <code>{sendResult}</code>
                </p>
              )}
            </section>
            )}
          </form>
        )}
      </div>
    </main>
  )
}
